import { z } from "zod";
import { cronGapSeconds, nextCronTick, parseCron } from "./cron.js";
import { CheckInLevel, CheckInSlug, CheckInStatus } from "./wire.js";

/**
 * Scheduled-job check-ins: "job X must report every N, with a grace period". The due-time math is pure
 * and lives here so core, api and the web app agree on it.
 */

/**
 * `new`: watched, no run reported yet. `ok`: the last run was fine and the next is not overdue.
 * `late`: a run was missed (a problem is open). `failed`: the last run reported a failure.
 * `paused`: a person paused it. `unscheduled`: the job reported but nobody said when it should run.
 */
export const CheckInState = z.enum(["new", "ok", "late", "failed", "paused", "unscheduled"]);
export type CheckInState = z.infer<typeof CheckInState>;

/** One check-in with its schedule and last run (AccountDO.listCheckIns, GET /v1/projects/:id/checkins). */
export const CheckInView = z.object({
  projectId: z.string(),
  slug: CheckInSlug,
  name: z.string().max(100),
  expectEverySeconds: z.number().int().positive().nullable(),
  cron: z.string().max(100).nullable(),
  /** The cron's zone as set; null means the account's zone (else UTC). */
  tz: z.string().max(64).nullable(),
  /** Grace in effect (the default when none was set). */
  graceSeconds: z.number().int().nonnegative(),
  level: CheckInLevel,
  /** A person edited it in the web app; the SDK's schedule no longer changes it. */
  editedInApp: z.boolean(),
  paused: z.boolean(),
  createdAt: z.number().int(),
  lastAt: z.number().int().nullable(),
  lastStatus: CheckInStatus.nullable(),
  lastDurationMs: z.number().int().nonnegative().nullable(),
  /** When the next run is expected (before grace); null when unscheduled or paused. */
  nextDueAt: z.number().int().nullable(),
  state: CheckInState,
  /** The open problem for this job, if any. */
  problemId: z.string().nullable(),
});
export type CheckInView = z.infer<typeof CheckInView>;

/**
 * A run reported a little before its cron time (clock skew) still counts for that time: the next due time
 * is the first tick after the run plus this much.
 */
export const CHECKIN_EARLY_TOLERANCE_MS = 90_000;

/** Default grace: a tenth of the usual gap, at least 5 minutes, at most 1 hour. */
export function defaultGraceSeconds(gapSeconds: number): number {
  return Math.min(3600, Math.max(300, Math.round(gapSeconds / 10)));
}

export type CheckInTiming = {
  expectEverySeconds: number | null;
  cron: string | null;
  tz: string | null;
  graceSeconds: number | null;
};

/** The zone a cron runs in: its own, else the account's, else UTC. */
export function cronZone(t: Pick<CheckInTiming, "tz">, accountTz: string | null): string {
  return t.tz ?? accountTz ?? "UTC";
}

/** Grace in effect, in seconds. */
export function effectiveGraceSeconds(t: CheckInTiming, fromMs: number, accountTz: string | null): number {
  if (t.graceSeconds !== null) return t.graceSeconds;
  if (t.expectEverySeconds !== null) return defaultGraceSeconds(t.expectEverySeconds);
  if (t.cron !== null) {
    try {
      const gap = cronGapSeconds(parseCron(t.cron), fromMs, cronZone(t, accountTz));
      if (gap !== null) return defaultGraceSeconds(gap);
    } catch {
      // A stored cron that no longer parses has no gap; the floor default applies.
    }
  }
  return defaultGraceSeconds(0);
}

/** When the run after `fromMs` is expected (`dueAt`) and when it counts as missed (`alertAt`). */
export type CheckInDue = { dueAt: number; alertAt: number };

/**
 * The next run expected after a run (or the check-in's creation) at `fromMs`. Interval schedules count
 * elapsed time, so DST never moves them; cron schedules follow the wall clock of their zone. Null when
 * there is no schedule or the cron never fires.
 */
export function nextCheckInDue(t: CheckInTiming, fromMs: number, accountTz: string | null): CheckInDue | null {
  let dueAt: number | null = null;
  if (t.expectEverySeconds !== null) dueAt = fromMs + t.expectEverySeconds * 1000;
  else if (t.cron !== null) {
    try {
      dueAt = nextCronTick(parseCron(t.cron), fromMs + CHECKIN_EARLY_TOLERANCE_MS, cronZone(t, accountTz));
    } catch {
      dueAt = null;
    }
  }
  if (dueAt === null) return null;
  return { dueAt, alertAt: dueAt + effectiveGraceSeconds(t, fromMs, accountTz) * 1000 };
}

/**
 * After a miss called at `due`: the following expected run, skipping any whose alert time has also
 * passed by `nowMs` (the account slept through several), so one outage never pages once per missed tick.
 */
export function followingCheckInDue(
  t: CheckInTiming,
  due: CheckInDue,
  nowMs: number,
  accountTz: string | null,
): CheckInDue | null {
  if (t.expectEverySeconds !== null) {
    const step = t.expectEverySeconds * 1000;
    const k = Math.max(1, Math.floor((nowMs - due.alertAt) / step) + 1);
    return { dueAt: due.dueAt + k * step, alertAt: due.alertAt + k * step };
  }
  let next: CheckInDue | null = nextCheckInDue(t, due.dueAt, accountTz);
  // Cron gaps are at least a minute; a year of minutes is the most an account could have slept through.
  for (let i = 0; i < 600_000 && next !== null && next.alertAt <= nowMs; i++) {
    next = nextCheckInDue(t, next.dueAt, accountTz);
  }
  return next;
}

/** "nightly-backup" → "Nightly backup". */
export function checkInName(slug: string): string {
  const words = slug.replace(/[._-]+/g, " ").trim();
  return words ? `${words[0]?.toUpperCase() ?? ""}${words.slice(1)}`.slice(0, 100) : slug.slice(0, 100);
}
